import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { Box, Typography } from '@material-ui/core';
import dealerDetailsStyles from './dealerDetailsStyles';
import SelectDealerButton from '../../buttons/selectDealerButton/SelectDealerButton';
import DealerDistance from './dealerDistance/DealerDistance';
import { GetDealers_dealers_nodes } from '../../../graphql/queries/__generated__/GetDealers';
import useCheckoutContext from '../../../../../common/hooks/useCheckoutContext';
import usePlatformConfig from '../../../../../../common/react/usePlatformConfig';
import {
  getEventDataCommonFields,
  vproductCommonFields,
  getRetailerCommonFields,
  getCheckoutStepsCommonFields,
  removeHostNameFromUrl,
} from '../../../../../common/utils/dxpDataLayerCommonFields';
import useNewDxpDigitalAssignmentEntryEvent from '../../../../../../gql/common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import useJourneyStepsContext from '../../../../../checkout4/hooks/useJourneyStepsContext';
import { DxpDataLayerEventNames } from '../../../../../../../../common/utilities/customDatalayerInterface';
import { EventDataTypes } from '../../../../../common/enum/DataLayerEnums';

export interface IDealerDetailsProps {
  orderId: number;
  dealer: GetDealers_dealers_nodes;
  isSelected: boolean;
  selectDealerId: (id: string) => void;
  disableDealerSelection?: boolean;
}

const DealerDetails: FunctionComponent<IDealerDetailsProps> = ({
  dealer,
  orderId,
  isSelected,
  selectDealerId,
  disableDealerSelection,
}) => {
  const styles = dealerDetailsStyles();
  const fieldRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    isSelected && fieldRef.current.scrollIntoView({ block: 'end' });
  }, []);
  const {
    platformConfig: { ga4DxpDataLayer },
  } = usePlatformConfig();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();
  const { steps, activeStep } = useJourneyStepsContext();
  const { deal } = useCheckoutContext();

  const handleDealerClick = useCallback(() => {
    selectDealerId(dealer.externalId);

    if (!deal) return;
    callDxpDigitalAssignmentEntryEvent(
      {
        ...getEventDataCommonFields(DxpDataLayerEventNames.RETAILER_DETAILS_CTA_CLICK, true),
        event_action: removeHostNameFromUrl(window.location.href),
        event_category: EventDataTypes.CHECKOUT_CLICK,
        event_label: `${EventDataTypes.BUTTON}::${EventDataTypes.SELECT_DEALER}`,
        event_pagecategory: EventDataTypes.CHECKOUT,
        event_non_int: 'false',
        click_url: removeHostNameFromUrl(window.location.href),
        click_text: EventDataTypes.SELECT_DEALER,
        click_name: EventDataTypes.BUTTON,
        vproduct: {
          ...vproductCommonFields(deal),
          est_delivery_time: deal?.car?.deliveryTimeFrame?.deliveryTimeFrameLabel ?? '',
          total_weight: '',
          max_speed: '',
        },
        checkoutStep: {
          ...getCheckoutStepsCommonFields(steps, activeStep),
        },
        retailer: {
          ...getRetailerCommonFields(deal, dealer),
        },
      },
      jlrDataLayer,
    );
  }, [selectDealerId, dealer.externalId, deal, jlrDataLayer, callDxpDigitalAssignmentEntryEvent, steps, activeStep]);

  return (
    <div className={styles.dealerDetailsContainer} ref={fieldRef}>
      <Box flexGrow={1}>
        <Box className={styles.dealerDetails}>
          <Typography variant={'subtitle2'} className={styles.dealerName} data-test={'text:dealer:name'}>
            {`${++orderId}. ${dealer.label}`}
          </Typography>
          <Typography
            variant={'body2'}
            component={'div'}
            data-test={'text:dealer:streetName'}
            className={styles.dealerAddress}
          >
            {dealer.address.address}
          </Typography>
          <Typography variant={'body2'} component={'div'} data-test={'text:dealer:city'} className={styles.dealerCity}>
            {dealer.address.city}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column">
        <DealerDistance dealer={dealer} />
        <SelectDealerButton
          isSelected={isSelected}
          onClick={handleDealerClick}
          testId={`dealer:select:button_${dealer.externalId}_${isSelected}`}
          disableDealerSelection={disableDealerSelection}
        />
      </Box>
    </div>
  );
};

export default DealerDetails;
